import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import ShippingForm from './checkout/ShippingForm';
import PaymentForm from './checkout/PaymentForm';
import OrderSummary from './checkout/OrderSummary';
import { generateCachedLampThumbnail } from '../utils/thumbnailGenerator';
import './Checkout.css';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

function Checkout() {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart, updateQuantity, removeFromCart, addToCart } = useCart();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Use order data if available (step 3), otherwise use cart data (steps 1-2)
  const displayItems = orderData?.items || cartItems;
  const displaySubtotal = orderData?.subtotal ?? getCartTotal();
  const displayShippingCost = orderData?.shippingCost ?? 19.99;
  const displayTax = orderData?.tax ?? (displaySubtotal * 0.1);
  const displayTotal = orderData?.totalAmount ?? (displaySubtotal + displayShippingCost + displayTax);


  useEffect(() => {
    // Redirect if cart is empty
    if (cartItems.length === 0 && currentStep === 1) {
      navigate('/cart');
    }
    // Note: Authentication is optional - users can checkout as guests
  }, [cartItems, currentStep, navigate]);

  const handleShippingSubmit = async (shippingData) => {
    setShippingInfo(shippingData);
    setError(null);

    // Create payment intent
    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      const requestUrl = `${apiUrl}/api/payments/create-intent`;
      console.log('Making request to:', requestUrl);
      console.log('Total amount:', displayTotal);
      
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      // Add auth header only if user is logged in
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          amount: displayTotal,
          currency: 'aud',
          metadata: {
            shippingAddress: JSON.stringify(shippingData)
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      const data = await response.json();
      setPaymentIntent(data);
      setCurrentStep(2);
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError('Failed to initialize payment. Please try again.');
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    setIsProcessing(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      // Add auth header only if user is logged in
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Generate thumbnails and upload to S3 before sending order
      const itemsWithThumbnails = await Promise.all(cartItems.map(async (item) => {
        // Check if item has design data
        const hasDesignData = item.design || (item.baseShape && item.modifiers);
        
        let thumbnailUrl = item.thumbnail || null;
        
        // Generate thumbnail if needed
        if (hasDesignData && !thumbnailUrl) {
          try {
            // Create a design object for thumbnail generation
            const designForThumbnail = item.design || {
              baseShape: item.baseShape,
              modifiers: item.modifiers || [],
              rendering: item.rendering || {},
              noiseSeed: item.noiseSeed,
              id: item.id || `temp-${Date.now()}-${Math.random()}`
            };
            
            // Generate thumbnail
            thumbnailUrl = await generateCachedLampThumbnail(designForThumbnail, {
              maxDimension: 512,
              includeWireframe: false
            });
          } catch (thumbnailError) {
            console.warn('Failed to generate thumbnail for item:', thumbnailError);
            // Continue without thumbnail if generation fails
          }
        }
        
        // Upload base64 thumbnail to S3 if it's a base64 data URL
        if (thumbnailUrl && thumbnailUrl.startsWith('data:image')) {
          try {
            const uploadResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/uploads/thumbnail`, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                base64Data: thumbnailUrl
              })
            });

            if (uploadResponse.ok) {
              const uploadData = await uploadResponse.json();
              thumbnailUrl = uploadData.url;
              console.log(`✅ Uploaded thumbnail to S3: ${thumbnailUrl}`);
            } else {
              const errorText = await uploadResponse.text();
              console.error('Failed to upload thumbnail to S3:', errorText);
              thumbnailUrl = null; // Don't send base64 - only S3 URLs accepted
            }
          } catch (uploadError) {
            console.error('Error uploading thumbnail to S3:', uploadError);
            thumbnailUrl = null; // Don't send base64 - only S3 URLs accepted
          }
        }
        
        return {
          ...item,
          thumbnail: thumbnailUrl
        };
      }));
      
      // Create order with items that have S3 thumbnail URLs only
      const orderResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: itemsWithThumbnails,
          shippingAddress: shippingInfo,
          paymentIntentId,
          totalAmount: displayTotal,
          subtotal: displaySubtotal,
          shippingCost: displayShippingCost,
          tax: displayTax
        })
      });

      if (!orderResponse.ok) {
        // Try to get error message from response
        let errorMessage = 'Failed to create order';
        try {
          const errorData = await orderResponse.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If response isn't JSON, use default message
        }
        throw new Error(errorMessage);
      }

      const order = await orderResponse.json();
      setOrderId(order.orderId);
      setOrderData(order); // Store order data for display

      // Clear cart
      clearCart();

      // Move to confirmation step
      setCurrentStep(3);
    } catch (err) {
      console.error('Error creating order:', err);
      // Show the actual error message if available
      const errorMessage = err.message || 'Failed to complete order. Please contact support.';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const steps = [
    { number: 1, title: 'Shipping', component: ShippingForm },
    { number: 2, title: 'Payment', component: PaymentForm },
    { number: 3, title: 'Confirmation', component: null }
  ];

  if (cartItems.length === 0 && currentStep < 3) {
    return null; // Will redirect
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>Checkout</h1>
          <div className="checkout-steps">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`checkout-step ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
              >
                <div className="step-number">{step.number}</div>
                <div className="step-title">{step.title}</div>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="checkout-error" role="alert">
            {error}
          </div>
        )}

        <div className="checkout-content">
          <div className="checkout-main">
            {currentStep === 1 && (
              <ShippingForm
                onSubmit={handleShippingSubmit}
                initialData={shippingInfo}
                user={user}
              />
            )}

            {currentStep === 2 && paymentIntent && (
              stripePromise ? (
                <Elements stripe={stripePromise} options={{ clientSecret: paymentIntent.clientSecret }}>
                  <PaymentForm
                    onSuccess={handlePaymentSuccess}
                    paymentIntentId={paymentIntent.paymentIntentId}
                    isProcessing={isProcessing}
                  />
                </Elements>
              ) : (
                <div className="checkout-error">
                  Stripe is not configured. Please set VITE_STRIPE_PUBLISHABLE_KEY in your environment variables.
                </div>
              )
            )}

            {currentStep === 3 && orderId && (
              <div className="order-confirmation">
                <div className="confirmation-icon">✓</div>
                <h2>Order Confirmed!</h2>
                <p className="order-number">Order #{orderId}</p>
                <p className="confirmation-message">
                  Thank you for your order. {shippingInfo?.email && `We've sent a confirmation email to ${shippingInfo.email}.`}
                </p>
                <div className="confirmation-actions">
                  {user && (
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="btn-primary"
                    >
                      View Order Details
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/shop')}
                    className={user ? 'btn-secondary' : 'btn-primary'}
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="checkout-sidebar">
            <OrderSummary
              items={displayItems}
              subtotal={displaySubtotal}
              shippingCost={displayShippingCost}
              tax={displayTax}
              total={displayTotal}
              onUpdateQuantity={currentStep < 3 ? updateQuantity : undefined}
              onRemoveItem={currentStep < 3 ? removeFromCart : undefined}
              onAddToCart={currentStep < 3 ? addToCart : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;

